import { useEffect, useState } from 'react';

import { Container, Flex, Toaster, FloraSwitch } from '@grupoboticario/flora-react';
import {
  ThumbUpOutlineIcon,
  ExclamationMarkChatBubbleIcon,
  ExclamationMarkHexagonIcon,
} from '@grupoboticario/flora-react-icons';
import debounce from 'debounce';
import slugify from 'slugify';

import { ErrorBoundaries, PageInfo, ResponsiveFloraTypography, Skeleton } from 'components';
import config from 'config';
import { OptOutType } from 'domains/opt-out';
import { getMFImagesLink } from 'helpers';
import { useCatalogOptOut, useUpdateCatalogOptOut, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';
import { useGlobalContext } from 'store';

import Styles from './OptOut.styles';
type TypeUseOptOut = { brand: keyof OptOutType; enable?: boolean };

const brandsList: { [key: string]: { img: string; name: string } } = {
  boticario: {
    name: 'oBoticário',
    img: `${getMFImagesLink()}logo-boticario.svg`,
  },
  eam: {
    name: 'Eu Amo Make',
    img: `${getMFImagesLink()}logo-euamomake.svg`,
  },
  eudora: {
    name: 'Eudora',
    img: `${getMFImagesLink()}logo-eudora.svg`,
  },
  oui: {
    name: 'O.U.I.',
    img: `${getMFImagesLink()}logo-oui.svg`,
  },
};

export const OptOut = () => {
  const {
    user: { isMultiBrand },
  } = useGlobalContext();
  const [brandToUpdate, setBrandToUpdate] = useState<TypeUseOptOut>({
    brand: 'boticario',
    enable: undefined,
  });
  const { data, refetch, isLoading, isError, isFetching, isSuccess } = useCatalogOptOut();
  const {
    isSuccess: isSuccessUpdate,
    isFetching: isFetchingUpdate,
    isError: isErrorUpdate,
    refetch: refetchUpdate,
    isFetched: isFetchedUpdate,
  } = useUpdateCatalogOptOut(brandToUpdate.brand, brandToUpdate.enable);
  const { sendDataToGTM } = useTracking();

  const brandCanBeInView = (brand: keyof OptOutType) => {
    const brandsInBoticario = ['boticario', 'oui', 'eam'];

    if (config.app.slugname === 'eudora' || (config.app.slugname === 'boticario' && isMultiBrand)) {
      return true;
    }

    return brandsInBoticario.includes(brand);
  };

  const catalogs = isSuccess
    ? (Object.keys(data) as (keyof OptOutType)[]).filter((brand) => brandCanBeInView(brand))
    : [];

  const handleOptOut = (enabled: boolean, brand: keyof OptOutType) => {
    setBrandToUpdate({ brand, enable: enabled });
  };

  useEffect(() => {
    if (brandToUpdate.enable !== undefined) {
      refetchUpdate();
    }
  }, [brandToUpdate, refetchUpdate]);

  useEffect(() => {
    if (brandToUpdate.enable !== undefined) {
      if (isSuccessUpdate && isFetchedUpdate && !isFetchingUpdate) {
        refetch();
        setBrandToUpdate({ brand: 'boticario', enable: undefined });

        sendDataToGTM({
          event: 'analytics-event', // always this event name
          event_parameters: {
            event_name: `callback_configurar_catalogo_sucesso`,
            cd_interaction_detail: `opt-${data?.[brandToUpdate.brand] ? 'out' : 'in'}:${slugify(
              brandsList[brandToUpdate.brand].name,
              { lower: true }
            )}`,
          },
        });

        Toaster.notify({
          kind: 'success',
          description: `O recebimento do catálogo foi alterado com sucesso!`,
          Icon: <ThumbUpOutlineIcon size={16} />,
          showIcon: true,
          origin: 'right-top',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUpdate, isFetchedUpdate, isFetchingUpdate]);

  useEffect(() => {
    if (brandToUpdate.enable !== undefined) {
      if (isErrorUpdate && isFetchedUpdate && !isFetchingUpdate) {
        setBrandToUpdate({ brand: 'boticario', enable: undefined });

        sendDataToGTM({
          event: 'analytics-event',
          event_parameters: {
            event_name: `callback_configurar_catalogo_erro`,
            cd_interaction_detail: `opt-${data?.[brandToUpdate.brand] ? 'out' : 'in'}:${slugify(
              brandsList[brandToUpdate.brand].name,
              { lower: true }
            )}`,
            cd_error_message: 'nao-foi-possivel-alterar-o-recebimento-do-catalogo',
          },
        });

        Toaster.notify({
          kind: 'error',
          description: `Não foi possível alterar o recebimento do catálogo! Tente novamente!`,
          Icon: <ExclamationMarkChatBubbleIcon size={16} />,
          showIcon: true,
          origin: 'right-top',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorUpdate, isFetchedUpdate, isFetchingUpdate]);

  useEffect(() => {
    if (isError) {
      newRelicMonitoringService.addPageAction('pageview_configurar_catalogo', {
        cd_interaction_detail: 'erro',
      });
    }
  }, [isError]);

  return (
    <Styles.Wrapper direction='column' data-autotagging-flora-section='catalogos_fisicos'>
      <Container size='large'>
        <PageInfo
          breadcrumbs={[
            {
              text: 'Configurar catálogo',
              url: '',
            },
          ]}
        >
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography fontSize='subtitle' as='h1' fontWeight='bold'>
              Configurar catálogo
            </ResponsiveFloraTypography>

            <ResponsiveFloraTypography>
              Configure o recebimento do catálogo físico
            </ResponsiveFloraTypography>
          </Flex>
        </PageInfo>
      </Container>

      <Styles.SectionWrapper>
        <Container size='large'>
          {isError && (
            <ErrorBoundaries.ReloadPage
              title='Tivemos um problema para carregar a página'
              data-autotagging-flora-section='catalogos_fisicos'
            />
          )}

          {isLoading && (
            <Styles.ListBrands>
              {[...Array(4)].map((_, index) => (
                <Styles.Card key={`skeleton-${index}`} data-testid='skeleton-opt'>
                  <Flex align='center' justify='space-between'>
                    <Skeleton height='$12' width='25%' />
                    <Skeleton height='$8' width='25%' />
                  </Flex>
                  <Styles.Divider variant='Line' />
                  <Skeleton height='$10' width='100%' />
                </Styles.Card>
              ))}
            </Styles.ListBrands>
          )}

          {!isError && !isLoading && (
            <Styles.Tag>
              <ExclamationMarkHexagonIcon size={24} />
              <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                Caso sua solicitação aconteça após o 1º pedido no ciclo, você só vai receber o
                catálogo físico no ciclo seguinte.
              </ResponsiveFloraTypography>
            </Styles.Tag>
          )}

          <Styles.ListBrands>
            {isSuccess &&
              catalogs.map((brand) => (
                <Styles.Card key={`${brand}-${data?.[brand]}`}>
                  <Flex align='center' justify='space-between'>
                    <Styles.LogoImage src={brandsList[brand].img} alt={brand || ''} />

                    <FloraSwitch
                      autotagging-flora='on'
                      data-flora='button'
                      data-flora-text={`opt-${data?.[brand] ? 'out' : 'in'}:${slugify(
                        brandsList[brand].name,
                        { lower: true }
                      )}`}
                      defaultChecked={data?.[brand]}
                      checked={isErrorUpdate ? data?.[brand] : undefined}
                      onChange={debounce(() => handleOptOut(!data?.[brand], brand), 500)}
                      disabled={isFetching || isFetchingUpdate}
                      data-brand={brand}
                      id={brand}
                    />
                  </Flex>

                  <Styles.Divider variant='Line' />

                  <ResponsiveFloraTypography
                    fontSize='bodySmallStandard'
                    data-enabled={data?.[brand]}
                    css={{
                      color: '$nonInteractiveAuxiliar',
                      alignItems: 'center',
                      '&[data-enabled="true"]': {
                        color: '$backgroundAltPrimary',
                      },
                    }}
                  >
                    {data?.[brand]
                      ? `Você vai receber o catálogo físico de ${brandsList[brand].name} do ciclo.`
                      : `No momento, você não vai receber o catálogo físico de ${brandsList[brand].name} do ciclo.`}
                  </ResponsiveFloraTypography>
                </Styles.Card>
              ))}
          </Styles.ListBrands>
        </Container>
      </Styles.SectionWrapper>
    </Styles.Wrapper>
  );
};
